import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Calendar, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const categories = ['All', 'Technology', 'Industry Insights', 'Safety & Compliance'];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/blog`)
      .then(r => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = posts.filter(p => {
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div className="pt-20">
      {/* HERO */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-red mb-4 block">Knowledge Hub</span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tighter font-heading mb-6">Insights & Industry Blog</h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Expert knowledge on industrial gases, safety standards, cryogenic technology, and industry best practices from the Seva Gases team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`blog-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex-shrink-0 px-5 py-2 text-sm font-semibold transition-colors duration-200 ${activeCategory === cat ? 'bg-brand-red text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="blog-search-input"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red"
              />
            </div>
          </div>
        </div>
      </section>

      {/* POSTS GRID */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-100 animate-pulse h-80" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24" data-testid="blog-empty">
              <p className="text-gray-500 text-lg">No articles found.</p>
            </div>
          ) : (
            <>
              {/* Featured post */}
              {filtered.length > 0 && activeCategory === 'All' && !search && (
                <motion.div {...fadeUp} className="mb-12" data-testid="blog-featured">
                  <Link to={`/blog/${filtered[0].slug}`} className="group grid grid-cols-1 lg:grid-cols-2 border border-gray-200 hover:border-brand-red transition-colors duration-200">
                    <div className="overflow-hidden">
                      <img src={filtered[0].image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80'} alt={filtered[0].title}
                        className="w-full h-64 lg:h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-10 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="section-tag">{filtered[0].category}</span>
                        <span className="text-gray-300">|</span>
                        <span className="text-gray-400 text-xs flex items-center gap-1"><Calendar size={12} />{formatDate(filtered[0].created_at)}</span>
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 font-heading mb-4 group-hover:text-brand-red transition-colors leading-tight">{filtered[0].title}</h2>
                      <p className="text-gray-600 text-sm leading-relaxed mb-6">{filtered[0].excerpt}</p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-gray-500 text-xs"><User size={12} />{filtered[0].author}</div>
                        <span className="text-brand-red text-sm font-semibold flex items-center gap-2 ml-auto">Read Article <ArrowRight size={14} /></span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(activeCategory === 'All' && !search ? filtered.slice(1) : filtered).map((post, i) => (
                  <motion.div key={post.id} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} data-testid={`blog-post-${i}`}>
                    <Link to={`/blog/${post.slug}`} className="group block bg-white border border-gray-200 hover:border-brand-red transition-all duration-200 h-full">
                      <div className="overflow-hidden">
                        <img src={post.image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80'} alt={post.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="section-tag">{post.category}</span>
                          <span className="text-gray-400 text-xs ml-auto flex items-center gap-1"><Calendar size={11} />{formatDate(post.created_at)}</span>
                        </div>
                        <h3 className="font-bold text-gray-900 font-heading text-lg mb-2 group-hover:text-brand-red transition-colors leading-snug line-clamp-2">{post.title}</h3>
                        <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <span className="text-gray-400 text-xs flex items-center gap-1"><User size={11} />{post.author}</span>
                          <span className="text-brand-red text-sm font-semibold flex items-center gap-1">Read <ArrowRight size={12} /></span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <span className="section-tag mb-3 block">Stay Updated</span>
            <h2 className="text-3xl font-bold text-gray-900 font-heading mb-4">Get Expert Insights Delivered</h2>
            <p className="text-gray-600 mb-8">Contact us to get notified about new industry articles and technical guides from Seva Gases.</p>
            <Link to="/contact" data-testid="blog-contact-btn" className="btn-primary">Get in Touch</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
